import RavnurPlayer from 'player';
import { component, prop, getter } from '../../../decorators';

import heartbeat from '../../../helpers/heartbeat.helper';

import invoke from 'lodash/invoke';
import result from 'lodash/result';
import map from 'lodash/map';

const HOUR = 3600; // sec

const SKIP_TIME = 3000; // ms

const DEFAULT_NEXT_PLAYBACK_RATE = 1;
const DEFAULT_PREV_PLAYBACK_RATE = -1;

const RATES = {
   [-8]: { prev: -8, next: -4 },
   [-4]: { prev: -8, next: -2 },
   [-2]: { prev: -4, next: -1 },
   [-1]: { prev: -2, next: 1 },
   1:  { prev: -1, next: 2 },
   2:  { prev: 1, next: 4 },
   4:  { prev: 2, next: 8 },
   8:  { prev: 4, next: 8 }
};

/* eslint camelcase: 0 */

const DEFAULT_OPTIONS = {
   flashPath: '/static/',
   hlsjsURL: '/static/hls.min.js',

   frameRate: 23.976,

   showTitle: false,
   showHelp: false,
   showFullScreen: true,
   showVolume: true,
   showBackward: true,
   showForward: true,
   showSettings: true,
   showNextFrame: true,
   showPrevFrame: true,
   showCCLayout: false,
   useMux: true,

   alwaysShowExtensions: true,

   hls: {
      maxFragLookUpTolerance: 0.001,
      maxMaxBufferLength: 20
   }
};

const MS_IN_SEC = 1000;

@component({
   watch: {
      sources: 'reload',
      hasPopups: 'onPopupsToggled'
   }
})
export default class Player {
   @prop({ required: true }) sources
   @prop({ default: false }) globalEvent
   @prop({ type: Object, default: () => ({}) }) opts

   @getter() hasPopups
   @getter() getCurrentUser
   @getter() getMuxApiKey

   get mux() {
      return {
         property_key: this.getMuxApiKey,
         viewer_user_id: this.getCurrentUser.id,
         video_id: this.sources.id,
         video_title: this.sources.title,
         video_content_type: 'episode',
         video_duration: this.sources.duration
      };
   }

   player = null
   time4frame = null
   timecode = null

   getCurrentTime() {
      return MS_IN_SEC * result(this.player, 'controller.getCurrentTime');
   }

   reload() {
      if (this.player) {
         this.player.destroy();
      }

      if (!this.sources) {
         return;
      }

      const source = { ...this.sources };

      const { mux } = this;

      this.time4frame = MS_IN_SEC / source.frameRate;
      this.timecode = source.timecode;
      source.timecode = source.timecode / MS_IN_SEC;

      source.clip = [ HOUR - source.timecode ];

      const opts = { ...DEFAULT_OPTIONS, ...this.opts, globalKeyboardListeners: this.globalEvent, mux };

      this.player = new RavnurPlayer(this.$el);
      this.player.setup(source, opts);
      this.player.on('timeupdate', this.onTimeupdate);
      this.player.on('pause', this.normalizeFrame);
   }

   onPopupsToggled() {
      if (this.player && this.globalEvent) {
         this.player.options.globalKeyboardListeners = !this.hasPopups;
      }
   }

   onTimeupdate() {
      heartbeat.ping();
      this.$emit('timeupdate', this.getCurrentTime());
   }

   normalizeFrame() {
      const time = this.getCurrentTime();
      const frame = Math.floor(time / this.time4frame);
      const toTime = Math.round(frame * this.time4frame);

      // fucking IE
      if (time !== toTime) {
         this.setCurrentTime(toTime);
      }
   }

   nextFrame() {
      invoke(this.player, 'controller.nextFrame');
   }

   prevFrame() {
      invoke(this.player, 'controller.prevFrame');
   }

   setVolume(volume) {
      invoke(this.player, 'controller.setVolume', volume);
   }

   toggle() {
      invoke(this.player, 'controller.setPlaybackRate', DEFAULT_NEXT_PLAYBACK_RATE);
      if (result(this.player, 'controller.isPaused')) {
         invoke(this.player, 'controller.play', true);
      } else {
         invoke(this.player, 'controller.pause', true);
      }
   }

   playForward() {
      this.rewind('next', DEFAULT_NEXT_PLAYBACK_RATE);
   }

   playBackward() {
      this.rewind('prev', DEFAULT_PREV_PLAYBACK_RATE);
   }

   rewind(key, defaultRate) {
      if (result(this.player, 'controller.isPaused')) {
         invoke(this.player, 'controller.play', true);
         this._setPlaybackRateSafely(defaultRate);
      } else {
         const current = result(this.player, 'controller.$el.playbackRate');
         const info = RATES[current] || RATES[defaultRate];
         this._setPlaybackRateSafely(info[key]);
      }
   }

   _setPlaybackRateSafely(rate) {
      try {
         invoke(this.player, 'controller.setPlaybackRate', rate);
      } catch(e) {
         invoke(this.player, 'controller.setPlaybackRate', DEFAULT_NEXT_PLAYBACK_RATE);
         const time = this.getCurrentTime() - SKIP_TIME;
         this.setCurrentTime(time);
      }
   }

   pause() {
      invoke(this.player, 'controller.pause', true);
      invoke(this.player, 'controller.setPlaybackRate', DEFAULT_NEXT_PLAYBACK_RATE);
   }

   setCurrentTime(time) {
      // time -= this.timecode;
      // time = Math.ceil(time / this.time4frame) * this.time4frame;
      // time += this.timecode;
      time = time / MS_IN_SEC;
      try {
         invoke(this.player, 'controller.setCurrentTime', time, true);
      } catch(e) {
         // noop
      }
   }

   refreshCaptions(transcripts) {
      if (this.player) {
         transcripts = map(transcripts, ({ from, to, text }) => {
            from = from / MS_IN_SEC;
            to = to / MS_IN_SEC;
            text = text.split('\n').join('<br/>');
            return { text, from, to };
         });
         this.player.bus.emit('refreshcaptions', transcripts);
      }
   }

   mounted() {
      this.reload();
   }

   beforeDestroy() {
      invoke(this.player, 'pause');
      invoke(this.player, 'destroy');
   }
}

